import type { CompletionBasis, CompletionPolicy, WorkAction, WorkPhase } from '@vwt/contracts';
import type { Violation } from './errors.js';

export const ACTIVE_PHASES: readonly WorkPhase[] = ['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'INTEGRATED'];
export const isActivePhase = (phase: WorkPhase) => ACTIVE_PHASES.includes(phase);

/** Who requests the transition. Integrations act as `system` with verified evidence only. */
export type WorkActorKind = 'user' | 'agent' | 'system';

export interface WorkState {
  phase: WorkPhase;
  completionPolicy: CompletionPolicy;
  ownerId: string | null;
  assigneeId: string | null;
  reviewerId: string | null;
  /** User who submitted the current outcome (for REVIEWED_DELIVERABLE separation of duties). */
  outcomeSubmittedById: string | null;
  contentRevision: number;
  outcomeContentRevision: number | null;
  requiresReview: boolean;
}

/** Facts gathered by the application layer before planning a transition. */
export interface CompletionFacts {
  openCompletionBlockers: number;
  /** Required children/prerequisites that are not DONE. */
  incompleteRequirements: Array<{ key: string; title: string }>;
  /** For PRODUCTION: delivery scope and verified coverage. */
  delivery?: {
    scopeConfirmed: boolean;
    requirementCount: number;
    allCovered: boolean;
    allChangesIntegrated: boolean;
  };
  /** Acceptance criteria still OPEN after applying the deliverable input. */
  openCriteria: number;
}

export interface WorkTransitionInput {
  reason?: string | undefined;
  outcomeMarkdown?: string | undefined;
  reviewerId?: string | undefined;
}

export interface WorkTransitionPlan {
  ok: boolean;
  to: WorkPhase;
  violations: Violation[];
  completionBasis: CompletionBasis | null;
  /** Starts a new completion episode (reopen). */
  opensEpisode: boolean;
  /** Ends the current completion episode with a completion. */
  completesEpisode: boolean;
}

interface Rule {
  action: WorkAction;
  from: readonly WorkPhase[];
  to: WorkPhase;
  policies?: readonly CompletionPolicy[];
  actors: readonly WorkActorKind[];
  requireReason?: boolean;
}

const ANY_ACTIVE = ACTIVE_PHASES;

/** Execution transition table (Section 5.5). */
export const WORK_TRANSITIONS: readonly Rule[] = [
  { action: 'start', from: ['TODO'], to: 'IN_PROGRESS', actors: ['user', 'agent'] },
  { action: 'request_review', from: ['IN_PROGRESS'], to: 'IN_REVIEW', actors: ['user', 'agent'] },
  { action: 'request_changes', from: ['IN_REVIEW'], to: 'IN_PROGRESS', actors: ['user'], requireReason: true },
  { action: 'integration_observed', from: ['IN_PROGRESS', 'IN_REVIEW'], to: 'INTEGRATED', policies: ['PRODUCTION'], actors: ['system'] },
  { action: 'production_verified', from: ANY_ACTIVE, to: 'DONE', policies: ['PRODUCTION'], actors: ['system'] },
  { action: 'complete_deliverable', from: ['IN_PROGRESS', 'IN_REVIEW'], to: 'DONE', policies: ['DELIVERABLE'], actors: ['user', 'agent'] },
  { action: 'approve_deliverable', from: ['IN_REVIEW'], to: 'DONE', policies: ['REVIEWED_DELIVERABLE'], actors: ['user'] },
  // The system cancels only to apply a project's automatic stale-work rule (ADR-0022), always with that rule as the reason.
  { action: 'cancel', from: ANY_ACTIVE, to: 'CANCELED', actors: ['user', 'system'], requireReason: true },
  { action: 'reopen', from: ['DONE', 'CANCELED'], to: 'IN_PROGRESS', actors: ['user', 'system'], requireReason: true },
  { action: 'coverage_lost', from: ['DONE'], to: 'INTEGRATED', policies: ['PRODUCTION'], actors: ['system'], requireReason: true },
];

export function workActionsFor(state: Pick<WorkState, 'phase' | 'completionPolicy'>, actor: WorkActorKind): WorkAction[] {
  return WORK_TRANSITIONS.filter(
    (r) => r.from.includes(state.phase) && (!r.policies || r.policies.includes(state.completionPolicy)) && r.actors.includes(actor),
  ).map((r) => r.action);
}

/**
 * Plans a guarded work transition. DONE is never an editable scalar: completion is derived
 * from policy-specific evidence, and agents cannot assert unproven production delivery.
 */
export function planWorkTransition(
  state: WorkState,
  action: WorkAction,
  actor: { kind: WorkActorKind; userId: string | null },
  facts: CompletionFacts,
  input: WorkTransitionInput = {},
): WorkTransitionPlan {
  const fail = (violations: Violation[]): WorkTransitionPlan => ({
    ok: false,
    to: state.phase,
    violations,
    completionBasis: null,
    opensEpisode: false,
    completesEpisode: false,
  });
  const rule = WORK_TRANSITIONS.find((r) => r.action === action);
  if (!rule) return fail([{ code: 'UNKNOWN_ACTION', message: `Unknown action ${action}` }]);
  if (rule.policies && !rule.policies.includes(state.completionPolicy)) {
    const hint =
      action === 'production_verified' || action === 'integration_observed'
        ? 'Production completion comes only from verified deployment evidence.'
        : '';
    return fail([
      { code: 'POLICY_MISMATCH', message: `${action} does not apply to ${state.completionPolicy} work. ${hint}`.trim() },
    ]);
  }
  if (!rule.actors.includes(actor.kind)) {
    const message =
      action === 'production_verified' || action === 'integration_observed' || action === 'coverage_lost'
        ? 'Only verified provider evidence can record integration or production delivery; it cannot be asserted manually.'
        : `This action cannot be performed by ${actor.kind === 'agent' ? 'an agent' : 'this actor'}`;
    return fail([{ code: 'ACTOR_NOT_ALLOWED', message }]);
  }
  if (!rule.from.includes(state.phase)) {
    return fail([{ code: 'TRANSITION_NOT_ALLOWED', message: `Cannot ${action} from ${state.phase}` }]);
  }
  const violations: Violation[] = [];
  if (rule.requireReason && !input.reason?.trim()) violations.push({ code: 'REASON_REQUIRED', message: 'A reason is required' });

  const completionGuards = () => {
    if (facts.openCompletionBlockers > 0) {
      violations.push({ code: 'BLOCKED', message: 'An unresolved blocker prevents completion' });
    }
    for (const req of facts.incompleteRequirements) {
      violations.push({ code: 'REQUIREMENT_INCOMPLETE', message: `${req.key} must be completed first` });
    }
  };

  let completionBasis: CompletionBasis | null = null;
  switch (action) {
    case 'request_review': {
      const needsReviewer = state.completionPolicy === 'REVIEWED_DELIVERABLE' || state.requiresReview;
      const reviewer = input.reviewerId ?? state.reviewerId;
      if (needsReviewer && !reviewer) violations.push({ code: 'REVIEWER_REQUIRED', message: 'Select a reviewer' });
      if (state.completionPolicy === 'REVIEWED_DELIVERABLE' && !input.outcomeMarkdown?.trim()) {
        violations.push({ code: 'OUTCOME_REQUIRED', message: 'Describe the outcome to be reviewed' });
      }
      if (reviewer && (reviewer === state.ownerId || reviewer === actor.userId) && state.completionPolicy === 'REVIEWED_DELIVERABLE') {
        violations.push({ code: 'REVIEWER_NOT_INDEPENDENT', message: 'The reviewer must differ from the owner and the outcome submitter' });
      }
      break;
    }
    case 'integration_observed':
      if (!facts.delivery?.allChangesIntegrated) {
        violations.push({ code: 'CHANGES_NOT_INTEGRATED', message: 'All declared required changes must be integrated' });
      }
      break;
    case 'production_verified':
      completionGuards();
      if (!facts.delivery?.scopeConfirmed || (facts.delivery?.requirementCount ?? 0) === 0) {
        violations.push({ code: 'SCOPE_NOT_CONFIRMED', message: 'Awaiting a confirmed, nonempty delivery scope' });
      } else if (!facts.delivery.allCovered) {
        violations.push({ code: 'COVERAGE_INCOMPLETE', message: 'Not every required change is verified in production' });
      }
      completionBasis = 'VERIFIED_PRODUCTION';
      break;
    case 'complete_deliverable':
      completionGuards();
      if (!input.outcomeMarkdown?.trim()) violations.push({ code: 'OUTCOME_REQUIRED', message: 'Record the outcome' });
      if (facts.openCriteria > 0) {
        violations.push({ code: 'CRITERIA_UNDISPOSITIONED', message: 'Mark each acceptance criterion as met, not met or waived' });
      }
      completionBasis = 'DELIVERABLE';
      break;
    case 'approve_deliverable':
      completionGuards();
      if (!actor.userId || actor.userId !== state.reviewerId) {
        violations.push({ code: 'NOT_DESIGNATED_REVIEWER', message: 'Only the designated reviewer can approve' });
      }
      if (actor.userId && (actor.userId === state.ownerId || actor.userId === state.outcomeSubmittedById)) {
        violations.push({ code: 'REVIEWER_NOT_INDEPENDENT', message: 'The reviewer must differ from the owner and the outcome submitter' });
      }
      if (state.outcomeContentRevision !== state.contentRevision) {
        violations.push({ code: 'OUTCOME_STALE', message: 'The content changed after the outcome was submitted; request review again' });
      }
      if (facts.openCriteria > 0) {
        violations.push({ code: 'CRITERIA_UNDISPOSITIONED', message: 'Every acceptance criterion needs a disposition' });
      }
      completionBasis = 'REVIEWED_DELIVERABLE';
      break;
    default:
      break;
  }
  if (violations.length) return fail(violations);
  return {
    ok: true,
    to: rule.to,
    violations: [],
    completionBasis,
    opensEpisode: action === 'reopen',
    completesEpisode: rule.to === 'DONE',
  };
}

/** Policy defaults (Section 5.5): deployable product changes → PRODUCTION, others → DELIVERABLE. */
export function defaultCompletionPolicy(type: string, projectRequiresReview = false): CompletionPolicy {
  if (['FEATURE', 'BUG', 'MAINTENANCE'].includes(type)) return 'PRODUCTION';
  return projectRequiresReview ? 'REVIEWED_DELIVERABLE' : 'DELIVERABLE';
}

/**
 * A policy change away from PRODUCTION requires a human-authorized reason; integrations
 * may never downgrade policy to force completion.
 */
export function validatePolicyChange(
  from: CompletionPolicy,
  to: CompletionPolicy,
  actor: WorkActorKind,
  reason: string | undefined,
): Violation[] {
  if (from === to) return [];
  if (from === 'PRODUCTION') {
    if (actor !== 'user') return [{ code: 'POLICY_DOWNGRADE_FORBIDDEN', message: 'Only a person can change a PRODUCTION policy' }];
    if (!reason?.trim()) return [{ code: 'REASON_REQUIRED', message: 'Explain why this work no longer requires production delivery' }];
  }
  return [];
}
